import { CommentType } from "../types";
import { CommentNode, CommentSiblingInfo, CommentTreeBuildResult } from "./types";
import { getTimestamp } from "./commentText";

function normalizeComment(comment: CommentType): CommentNode {
  return {
    ...comment,
    parentId: comment.parentId ?? null,
    replies: [],
    depth: 0,
    directReplyCount: 0,
    totalReplyCount: 0,
  };
}

export function collectComments(comments: CommentType[]): CommentType[] {
  const byId = new Map<number, CommentType>();

  const walk = (items: CommentType[] = []) => {
    items.forEach((comment) => {
      if (!comment || typeof comment.id !== "number") return;
      byId.set(comment.id, {
        ...comment,
        parentId: comment.parentId ?? null,
      });
      if (comment.replies?.length) walk(comment.replies);
    });
  };

  walk(comments);
  return Array.from(byId.values());
}

function sortByCreatedAtThenId(a: CommentNode, b: CommentNode): number {
  const byTime = getTimestamp(a.createdAt) - getTimestamp(b.createdAt);
  if (byTime !== 0) return byTime;
  return a.id - b.id;
}

function refreshCountsAndDepth(node: CommentNode, depth: number): number {
  node.depth = depth;
  node.replies.sort(sortByCreatedAtThenId);
  node.directReplyCount = node.replies.length;

  let descendants = 0;
  node.replies.forEach((reply) => {
    descendants += 1 + refreshCountsAndDepth(reply, depth + 1);
  });

  node.totalReplyCount = descendants;
  return descendants;
}

export function buildCommentTree(comments: CommentType[]): CommentTreeBuildResult {
  const flat = collectComments(comments);
  const byId = new Map<number, CommentNode>();

  flat.forEach((comment) => {
    byId.set(comment.id, normalizeComment(comment));
  });

  const roots: CommentNode[] = [];
  const orphans: CommentNode[] = [];

  byId.forEach((node) => {
    if (node.parentId === null) {
      roots.push(node);
      return;
    }

    const parent = byId.get(node.parentId);
    if (parent) {
      parent.replies.push(node);
    } else {
      // Keep orphaned comments visible rather than dropping them silently.
      // This protects the Explorer from inconsistent legacy data.
      orphans.push(node);
      roots.push(node);
    }
  });

  roots.sort(sortByCreatedAtThenId);
  roots.forEach((root) => refreshCountsAndDepth(root, 0));

  return {
    roots,
    all: flattenCommentTree(roots),
    byId,
    orphans,
  };
}

export function flattenCommentTree(roots: CommentNode[]): CommentNode[] {
  const result: CommentNode[] = [];

  const walk = (node: CommentNode) => {
    result.push(node);
    node.replies.forEach(walk);
  };

  roots.forEach(walk);
  return result;
}

export function getCommentById(
  allOrById: CommentNode[] | Map<number, CommentNode>,
  id: number | null | undefined
): CommentNode | null {
  if (typeof id !== "number") return null;
  if (allOrById instanceof Map) return allOrById.get(id) ?? null;
  return allOrById.find((comment) => comment.id === id) ?? null;
}

export function getCommentPath(
  byId: Map<number, CommentNode>,
  commentId: number | null | undefined
): CommentNode[] {
  if (typeof commentId !== "number") return [];

  const path: CommentNode[] = [];
  const seen = new Set<number>();
  let current = byId.get(commentId) ?? null;

  while (current && !seen.has(current.id)) {
    path.unshift(current);
    seen.add(current.id);
    current = typeof current.parentId === "number" ? byId.get(current.parentId) ?? null : null;
  }

  return path;
}

export function getRootForComment(
  byId: Map<number, CommentNode>,
  comment: CommentNode | null | undefined
): CommentNode | null {
  if (!comment) return null;
  const path = getCommentPath(byId, comment.id);
  return path[0] ?? comment;
}

export function countDirectReplies(comment: CommentNode | null | undefined): number {
  return comment?.replies.length ?? 0;
}

export function countAllReplies(comment: CommentNode | null | undefined): number {
  if (!comment) return 0;
  return comment.replies.reduce((total, reply) => total + 1 + countAllReplies(reply), 0);
}

export function getCommentDepth(
  byId: Map<number, CommentNode>,
  comment: CommentNode | null | undefined
): number {
  if (!comment) return 0;
  return Math.max(0, getCommentPath(byId, comment.id).length - 1);
}

export function getSiblingInfo(
  byId: Map<number, CommentNode>,
  comment: CommentNode | null | undefined
): CommentSiblingInfo {
  if (!comment) return { index: 0, count: 0 };

  let siblings: CommentNode[] = [];
  if (typeof comment.parentId === "number") {
    siblings = byId.get(comment.parentId)?.replies ?? [];
  } else {
    siblings = Array.from(byId.values())
      .filter((candidate) => candidate.parentId === null)
      .sort(sortByCreatedAtThenId);
  }

  return {
    index: Math.max(0, siblings.findIndex((candidate) => candidate.id === comment.id)),
    count: siblings.length,
  };
}

export function getRecentComments(comments: CommentNode[]): CommentNode[] {
  return [...comments].sort((a, b) => {
    const byTime = getTimestamp(b.createdAt) - getTimestamp(a.createdAt);
    if (byTime !== 0) return byTime;
    return b.id - a.id;
  });
}

export function findFirstSelectableComment(roots: CommentNode[]): CommentNode | null {
  return roots[0] ?? null;
}
